/*
  Flower Box Sale: flower renderer (bloom-flowers.js)
  Draws one painterly flower on a 2D canvas. Used by flower-box-sale.js.
  Plain script, no libraries. Exposes window.BloomFlowers (see SPEC section 4).
  Coordinates: (0,0) is the base of the stem, the flower grows toward negative y.
  Species: tulip (Sculpt Studio Box), daffodil, cosmos, poppy, iris, rose, zinnia.
  Still drawable for older product settings: daisy, peony, peony-bud, garden-rose.
*/
(function () {
  'use strict';

  var PI = Math.PI, TAU = PI * 2;

  // Natural proportions (shared with the meadow).
  var SPECIES = {
    'peony':       { height: 1.0,  headRatio: 0.42 },
    'peony-bud':   { height: 0.72, headRatio: 0.30 },
    'cosmos':      { height: 1.05, headRatio: 0.36 },
    'poppy':       { height: 0.95, headRatio: 0.38 },
    'iris':        { height: 1.1,  headRatio: 0.40 },
    'garden-rose': { height: 0.9,  headRatio: 0.40 },
    'rose':        { height: 0.9,  headRatio: 0.40 },
    'zinnia':      { height: 0.85, headRatio: 0.38 },
    'daisy':       { height: 0.95, headRatio: 0.40 },
    'daffodil':    { height: 1.0,  headRatio: 0.36 },
    'tulip':       { height: 1.05, headRatio: 0.30 }
  };

  // Internal drawing traits per species.
  // top: how far the bloom rises above the stem tip (in head radii).
  // leaf: [type, length, width, teeth, toothDepth, curl] in flower heights.
  var GREENS = {
    leaf: [[127, 168, 102], [94, 138, 78], [52, 92, 50]],
    grey: [[150, 168, 128], [104, 128, 92], [66, 90, 60]],
    blue: [[122, 158, 140], [82, 124, 108], [46, 84, 72]],
    deep: [[112, 150, 92], [72, 112, 62], [38, 70, 40]],
    sage: [[134, 170, 124], [86, 130, 94], [46, 86, 60]]
  };
  var TRAITS = {
    'peony':       { top: 0.86, stemW: 1.0, greens: 'leaf', leaf: ['blade', 0.27, 0.075, 3, 0.38, 0.22] },
    'peony-bud':   { top: 0.95, stemW: 0.9, greens: 'leaf', leaf: ['blade', 0.3, 0.08, 3, 0.36, 0.22] },
    'cosmos':      { top: 0.78, stemW: 0.7, greens: 'leaf', leaf: ['feather', 0.24, 0.07] },
    'poppy':       { top: 0.98, stemW: 0.8, greens: 'grey', leaf: ['blade', 0.24, 0.07, 5, 0.3, 0.12], hairy: true },
    'iris':        { top: 1.02, stemW: 1.0, greens: 'blue', leaf: ['sword'] },
    'garden-rose': { top: 0.82, stemW: 1.0, greens: 'deep', leaf: ['rose', 0.13, 0.05, 10, 0.09, 0.1] },
    'rose':        { top: 0.82, stemW: 1.0, greens: 'deep', leaf: ['rose', 0.13, 0.05, 10, 0.09, 0.1] },
    'zinnia':      { top: 0.7,  stemW: 1.0, greens: 'leaf', leaf: ['blade', 0.24, 0.06, 0, 0, 0.18] },
    'daisy':       { top: 0.74, stemW: 0.8, greens: 'leaf', leaf: ['blade', 0.2, 0.06, 4, 0.34, 0.16] },
    'daffodil':    { top: 0.95, stemW: 0.9, greens: 'blue', leaf: ['sword'] },
    // Tulip: straight stem (bends scaled by straight), two broad basal strap leaves, the cup
    // rises 1.9 head radii above the stem tip and its center sits 0.95 above it (hcy).
    'tulip':       { top: 1.9, stemW: 1.1, greens: 'sage', leaf: ['strap'], straight: 0.3, hcy: 0.95 }
  };

  var WHITE = [255, 255, 255], WINE = [74, 26, 42], INK = [26, 16, 30];

  /* ---------- small helpers ---------- */
  function c01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function ease(t) { t = c01(t); return 1 - (1 - t) * (1 - t) * (1 - t); }
  function span(g, a, b) { return ease((g - a) / (b - a)); }
  function lerp(a, b, t) { return a + (b - a) * t; }

  // Deterministic random numbers (mulberry32).
  function rng(seed) {
    var s = (seed >>> 0) || 1;
    return function () {
      s = (s + 0x6D2B79F5) | 0;
      var t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  var hexCache = {};
  function rgb(hex) {
    if (Array.isArray(hex)) return hex;
    var k = String(hex || '');
    if (hexCache[k]) return hexCache[k];
    var h = k.replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var out = /^[0-9a-f]{6}$/i.test(h)
      ? [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
      : [233, 154, 140];
    hexCache[k] = out;
    return out;
  }
  function mix(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
  function css(c, a) { return 'rgba(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ',' + (a == null ? 1 : a) + ')'; }
  function hex(c) { return '#' + c.map(function (v) { return ('0' + Math.round(v).toString(16)).slice(-2); }).join(''); }
  function lum(c) { return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255; }
  // Highlight mix toward white. Dark shades (deep glitter, black gel) get extra light on
  // the petal tips and rims so they still read as petals rather than flat dark shapes.
  var LIFT = 0;
  function lift(c, t) { return mix(c, WHITE, Math.min(0.92, t + LIFT)); }
  function liftFor(c) { return Math.max(0, 0.55 - lum(c)) * 1.35; }

  // Deep shade of the same hue, used for petal bases.
  function deepOf(c) { return mix([c[0] * 0.42, c[1] * 0.38, c[2] * 0.42], WINE, 0.3); }

  function grad(ctx, x0, y0, x1, y1, stops) {
    var g = ctx.createLinearGradient(x0, y0, x1, y1);
    for (var i = 0; i < stops.length; i += 2) g.addColorStop(stops[i], stops[i + 1]);
    return g;
  }
  function radial(ctx, x, y, r, stops) {
    var g = ctx.createRadialGradient(x, y, 0, x, y, r);
    for (var i = 0; i < stops.length; i += 2) g.addColorStop(stops[i], stops[i + 1]);
    return g;
  }

  /* ---------- petal outlines (pointing to -y, base at 0,0) ---------- */
  function petalPath(ctx, shape, L, W, ruf) {
    var t = -L;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    if (shape === 'ruffle') { // peony: rounded, ruffled tip (from the approved reference)
      ctx.bezierCurveTo(-W * 0.75, t * 0.2, -W * 0.95, t * 0.75, -W * 0.45, t * 0.97);
      ctx.quadraticCurveTo(-W * 0.3, t * 1.04 + ruf, -W * 0.12, t * 0.99);
      ctx.quadraticCurveTo(0, t * 1.06, W * 0.12, t * 0.99);
      ctx.quadraticCurveTo(W * 0.3, t * 1.04 - ruf, W * 0.45, t * 0.97);
      ctx.bezierCurveTo(W * 0.95, t * 0.75, W * 0.75, t * 0.2, 0, 0);
    } else if (shape === 'wedge') { // cosmos: narrow base, broad toothed tip
      ctx.bezierCurveTo(-W * 0.16, t * 0.25, -W * 0.52, t * 0.55, -W * 0.5, t * 0.9);
      ctx.quadraticCurveTo(-W * 0.42, t * 1.03, -W * 0.3, t * 0.97);
      ctx.quadraticCurveTo(-W * 0.16, t * 1.05 + ruf, 0, t * 0.98);
      ctx.quadraticCurveTo(W * 0.16, t * 1.05 - ruf, W * 0.3, t * 0.97);
      ctx.quadraticCurveTo(W * 0.42, t * 1.03, W * 0.5, t * 0.9);
      ctx.bezierCurveTo(W * 0.52, t * 0.55, W * 0.16, t * 0.25, 0, 0);
    } else if (shape === 'broad') { // poppy and iris falls: wide, silky, wavy edge
      ctx.bezierCurveTo(-W * 0.55, t * 0.08, -W * 1.0, t * 0.5, -W * 0.82, t * 0.84);
      ctx.quadraticCurveTo(-W * 0.6, t * 1.07 + ruf, -W * 0.28, t * 0.98);
      ctx.quadraticCurveTo(0, t * 1.07, W * 0.26, t * 0.99);
      ctx.quadraticCurveTo(W * 0.6, t * 1.06 - ruf, W * 0.82, t * 0.84);
      ctx.bezierCurveTo(W * 1.0, t * 0.5, W * 0.55, t * 0.08, 0, 0);
    } else if (shape === 'obovate') { // iris standards: narrow claw, rounded blade
      ctx.bezierCurveTo(-W * 0.2, t * 0.3, -W * 0.7, t * 0.55, -W * 0.55, t * 0.86);
      ctx.quadraticCurveTo(-W * 0.32, t * 1.06 + ruf, 0, t);
      ctx.quadraticCurveTo(W * 0.32, t * 1.06 - ruf, W * 0.55, t * 0.86);
      ctx.bezierCurveTo(W * 0.7, t * 0.55, W * 0.2, t * 0.3, 0, 0);
    } else if (shape === 'point') { // zinnia: long oval to a soft point
      ctx.bezierCurveTo(-W * 0.62, t * 0.18, -W * 0.6, t * 0.74, -W * 0.08, t * 0.98);
      ctx.quadraticCurveTo(0, t * 1.03, W * 0.08, t * 0.98);
      ctx.bezierCurveTo(W * 0.6, t * 0.74, W * 0.62, t * 0.18, 0, 0);
    } else if (shape === 'strap') { // daisy: long narrow ray, rounded tip with a faint notch
      ctx.bezierCurveTo(-W * 0.3, t * 0.04, -W * 0.5, t * 0.22, -W * 0.5, t * 0.55);
      ctx.bezierCurveTo(-W * 0.5, t * 0.84, -W * 0.46, t * 0.99, -W * 0.22, t * 1.0);
      ctx.quadraticCurveTo(-W * 0.08, t * 1.01, 0, t * 0.965 + ruf);
      ctx.quadraticCurveTo(W * 0.08, t * 1.01, W * 0.22, t * 1.0);
      ctx.bezierCurveTo(W * 0.46, t * 0.99, W * 0.5, t * 0.84, W * 0.5, t * 0.55);
      ctx.bezierCurveTo(W * 0.5, t * 0.22, W * 0.3, t * 0.04, 0, 0);
    } else if (shape === 'tepal') { // daffodil: broad ovate segment tapering to a sharp point
      ctx.bezierCurveTo(-W * 0.5, t * 0.04, -W * 0.66, t * 0.38, -W * 0.42, t * 0.68);
      ctx.bezierCurveTo(-W * 0.28, t * 0.84 + ruf, -W * 0.08, t * 0.93, 0, t);
      ctx.bezierCurveTo(W * 0.08, t * 0.93, W * 0.28, t * 0.84 - ruf, W * 0.42, t * 0.68);
      ctx.bezierCurveTo(W * 0.66, t * 0.38, W * 0.5, t * 0.04, 0, 0);
    } else if (shape === 'tulip') { // tulip tepal: ovate, widest above the middle, a soft point
      ctx.bezierCurveTo(-W * 0.4, t * 0.02, -W * 0.6, t * 0.36, -W * 0.56, t * 0.64);
      ctx.bezierCurveTo(-W * 0.52, t * 0.86, -W * 0.26, t * 0.95 + ruf, -W * 0.06, t * 0.99);
      ctx.quadraticCurveTo(0, t * 1.012, W * 0.06, t * 0.99);
      ctx.bezierCurveTo(W * 0.26, t * 0.95 - ruf, W * 0.52, t * 0.86, W * 0.56, t * 0.64);
      ctx.bezierCurveTo(W * 0.6, t * 0.36, W * 0.4, t * 0.02, 0, 0);
    } else { // cup: rose guard petal
      ctx.bezierCurveTo(-W * 0.72, t * 0.04, -W * 0.8, t * 0.78, -W * 0.36, t * 0.97);
      ctx.quadraticCurveTo(0, t * 1.08 + ruf, W * 0.36, t * 0.97);
      ctx.bezierCurveTo(W * 0.8, t * 0.78, W * 0.72, t * 0.04, 0, 0);
    }
    ctx.closePath();
  }

  // Fills the current petal path with shading, veins, glitter flecks and an edge.
  function shade(ctx, P, fill, L, W, veins, r) {
    ctx.globalAlpha = P.alpha;
    ctx.fillStyle = fill;
    ctx.fill();
    if (P.detail && veins) {
      ctx.globalAlpha = P.alpha * (P.glass ? 0.4 : 0.14);
      ctx.strokeStyle = P.veinCol;
      ctx.lineWidth = 0.6 * P.px;
      ctx.beginPath();
      for (var v = 0; v < veins; v++) {
        var k = veins > 1 ? (v / (veins - 1)) * 2 - 1 : 0;
        ctx.moveTo(k * W * 0.04, -L * 0.06);
        ctx.quadraticCurveTo(k * W * 0.22, -L * 0.5, k * W * (0.3 + r() * 0.06), -L * (0.82 + r() * 0.08));
      }
      ctx.stroke();
    }
    if (P.glitter && P.detail) {
      ctx.save();
      ctx.clip();
      ctx.globalAlpha = 1;
      for (var g = 0; g < 9; g++) {
        var gx = (r() - 0.5) * W * 1.2, gy = -L * (0.15 + r() * 0.85), gs = (0.5 + r() * 0.9) * P.px;
        ctx.fillStyle = r() < 0.6 ? 'rgba(255,255,255,0.85)' : css(mix(P.c, WHITE, 0.6), 0.9);
        ctx.fillRect(gx, gy, gs, gs);
      }
      ctx.restore();
    }
    ctx.globalAlpha = P.glass ? 0.95 : P.alpha * 0.4;
    ctx.strokeStyle = P.edgeCol;
    ctx.lineWidth = (P.glass ? 1.1 : 0.55) * P.px;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Draws a ring of petals, back petals first so front petals overlap.
  // ring: { n, L, W, shape, ruf, offset, jitter, spread, sx, sy, fill, veins, y }
  function ring(ctx, P, R, r) {
    var list = [];
    for (var i = 0; i < R.n; i++) {
      var a = (360 / R.n) * i + R.offset + (r() - 0.5) * R.jitter;
      a = ((a + 180) % 360 + 360) % 360 - 180;
      list.push({ a: a * PI / 180, L: R.L * (0.9 + r() * 0.18), W: R.W * (0.86 + r() * 0.24), ruf: R.ruf * (r() - 0.3), s: r() * 1e9 });
    }
    list.sort(function (p, q) { return Math.abs(p.a) - Math.abs(q.a); });
    for (var j = 0; j < list.length; j++) {
      var p = list[j];
      ctx.save();
      if (R.y) ctx.translate(0, R.y);
      ctx.rotate(p.a * R.spread);
      ctx.scale(R.sx, R.sy);
      petalPath(ctx, R.shape, p.L, p.W, p.ruf);
      shade(ctx, P, R.fill, p.L, p.W, R.veins, rng(p.s));
      ctx.restore();
    }
  }

  function petalFill(ctx, L, stops) { return grad(ctx, 0, 0, 0, -L, stops); }

  // Soft shadow pooled in the middle of a flower, gives cupped depth.
  function hollow(ctx, rad, a, y) {
    ctx.fillStyle = radial(ctx, 0, y || 0, rad, [0, 'rgba(40,14,24,' + a + ')', 1, 'rgba(40,14,24,0)']);
    ctx.beginPath(); ctx.arc(0, y || 0, rad, 0, TAU); ctx.fill();
  }

  // Green sepals cupping the bud.
  function sepals(ctx, P, n, len, wid, greens, r, spreadDeg) {
    var g = GREENS[greens] || GREENS.leaf;
    for (var i = 0; i < n; i++) {
      var a = (i / (n - 1 || 1) - 0.5) * spreadDeg * PI / 180 + PI + (r() - 0.5) * 0.2;
      ctx.save();
      ctx.rotate(a);
      ctx.beginPath();
      ctx.moveTo(-wid, 0);
      ctx.quadraticCurveTo(-wid * 0.9, -len * 0.6, 0, -len);
      ctx.quadraticCurveTo(wid * 0.9, -len * 0.6, wid, 0);
      ctx.closePath();
      ctx.fillStyle = grad(ctx, 0, 0, 0, -len, [0, css(g[2]), 1, css(g[0])]);
      ctx.fill();
      ctx.restore();
    }
  }

  /* ---------- species heads (unit: 1 = head radius) ---------- */

  function peony(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var layers = [[11, 1, 0.62, 0.08, 0.38, 0.05], [10, 0.81, 0.51, 0.07, 0.30, 0], [9, 0.62, 0.41, 0.055, 0.22, 0], [8, 0.43, 0.3, 0.04, 0.45, 0.1]];
    ctx.save();
    ctx.scale(1, 0.78);
    for (var li = 0; li < 4; li++) {
      var s = layers[li];
      var o = span(open, li * 0.1, 0.62 + li * 0.1);
      if (li > 0) hollow(ctx, s[1] * 0.95, 0.16 * P.alpha, 0);
      var fill = petalFill(ctx, s[1], [0, css(mix(c, acc, s[4])), 0.55, css(c), 1, css(lift(c, 0.45 + s[5]))]);
      ring(ctx, P, {
        n: s[0], L: s[1], W: s[2], ruf: s[3], shape: 'ruffle', offset: li * 17, jitter: 14,
        spread: 0.22 + 0.78 * o, sx: 0.55 + 0.45 * o, sy: 0.32 + 0.68 * o,
        fill: fill, veins: li < 2 ? 3 : 0, y: -0.03 * li
      }, r);
    }
    // Petaloid center
    var hp = 0.6 + 0.4 * ease(open);
    for (var i = 0; i < 14; i++) {
      var a = r() * TAU, rr = r() * 0.1 * hp, tone = 0.08 + r() * 0.16;
      ctx.globalAlpha = 0.9 * P.alpha;
      ctx.fillStyle = css(mix(c, acc, tone));
      ctx.beginPath();
      ctx.ellipse(Math.cos(a) * rr, Math.sin(a) * rr - 0.12, 0.08 * hp, 0.055 * hp, a, 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  // Peony bud seen from the side: a shaded globe of petals held by sepals, opening halfway.
  function peonyBud(ctx, P, r) {
    var c = P.c, acc = P.acc, o = span(P.open, 0, 1) * 0.65;
    var g = GREENS.leaf, gy = -0.5, rx = 0.5 + 0.08 * o, ry = 0.55;
    ctx.scale(1.15, 1.15);
    // Sepals behind the bud, cupping it from below
    for (var i = 0; i < 4; i++) {
      var sa = [-72, -42, 42, 72][i] * PI / 180 * (1 + 0.2 * o) + (r() - 0.5) * 0.15;
      ctx.save();
      ctx.translate(0, 0.04);
      ctx.rotate(sa);
      ctx.beginPath();
      ctx.moveTo(-0.13, 0);
      ctx.quadraticCurveTo(-0.18, -0.5, 0.04, -0.8);
      ctx.quadraticCurveTo(0.16, -0.45, 0.13, 0);
      ctx.closePath();
      ctx.fillStyle = grad(ctx, 0, 0, 0, -0.66, [0, css(g[2]), 1, css(g[0])]);
      ctx.fill();
      ctx.restore();
    }
    // Globe of tightly folded petals
    ctx.globalAlpha = P.alpha;
    ctx.fillStyle = grad(ctx, 0, gy + ry, 0, gy - ry, [0, css(mix(c, acc, 0.5)), 0.5, css(c), 1, css(lift(c, 0.42))]);
    ctx.beginPath(); ctx.ellipse(0, gy, rx, ry, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = radial(ctx, -rx * 0.35, gy - ry * 0.35, rx * 0.8, [0, 'rgba(255,255,255,0.32)', 1, 'rgba(255,255,255,0)']);
    ctx.fill();
    if (P.detail) {
      ctx.strokeStyle = P.veinCol; ctx.globalAlpha = 0.16 * P.alpha; ctx.lineWidth = 0.6 * P.px;
      ctx.beginPath();
      for (var v = -2; v <= 2; v++) { ctx.moveTo(v * 0.08, gy + ry * 0.9); ctx.quadraticCurveTo(v * 0.28, gy, v * 0.1, gy - ry * 0.9); }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    // Ruffled tips unfolding at the top
    ctx.save();
    ctx.translate(0, gy - ry * 0.62);
    ctx.scale(1, 0.42);
    ring(ctx, P, {
      n: 7, L: 0.34 + 0.1 * o, W: 0.3, ruf: 0.05, shape: 'ruffle', offset: r() * 50, jitter: 18,
      spread: 0.45 + 0.55 * o, sx: 0.8, sy: 0.6 + 0.4 * o,
      fill: petalFill(ctx, 0.4, [0, css(mix(c, acc, 0.35)), 1, css(lift(c, 0.5))]), veins: 0
    }, r);
    ctx.restore();
    // Outer petals wrapping the globe, sides first, the front petal last
    var wrap = [[-1, 0.86], [1, 0.86], [-0.5, 0.92], [0.5, 0.92], [0, 0.8]];
    var wf = petalFill(ctx, 0.95, [0, css(mix(c, acc, 0.42)), 0.55, css(c), 1, css(lift(c, 0.4))]);
    for (var k = 0; k < wrap.length; k++) {
      var w = wrap[k];
      ctx.save();
      ctx.translate(w[0] * 0.08, 0.06);
      ctx.rotate(w[0] * (34 + 26 * o) * PI / 180);
      ctx.scale(1 - Math.abs(w[0]) * 0.25, 1);
      petalPath(ctx, 'cup', w[1], 0.66, 0.05 * (r() - 0.5));
      shade(ctx, P, wf, w[1], 0.66, 3, r);
      ctx.restore();
    }
  }

  function cosmos(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    ctx.save();
    ctx.rotate((r() - 0.5) * 0.3);
    ctx.scale(1, 0.74);
    var fill = petalFill(ctx, 1, [0, css(mix(c, acc, 0.5)), 0.22, css(mix(c, acc, 0.18)), 0.6, css(c), 1, css(lift(c, 0.38))]);
    var o = span(open, 0, 0.85);
    ring(ctx, P, {
      n: 8, L: 1, W: 0.56, ruf: 0.035, shape: 'wedge', offset: r() * 45, jitter: 10,
      spread: 0.15 + 0.85 * o, sx: 0.5 + 0.5 * o, sy: 0.35 + 0.65 * o, fill: fill, veins: 5
    }, r);
    // Golden disc with tiny florets
    var dr = 0.2 * (0.6 + 0.4 * o);
    ctx.fillStyle = radial(ctx, -dr * 0.3, -dr * 0.35, dr * 1.4, [0, '#FBE08A', 0.45, '#E9B23A', 0.85, '#B47A1A', 1, '#7A4E12']);
    ctx.beginPath(); ctx.arc(0, 0, dr, 0, TAU); ctx.fill();
    if (P.detail) {
      ctx.fillStyle = 'rgba(122,78,18,0.55)';
      for (var i = 0; i < 18; i++) {
        var a = i * 2.39996, rr = dr * Math.sqrt((i + 0.5) / 18) * 0.85;
        ctx.beginPath(); ctx.arc(Math.cos(a) * rr, Math.sin(a) * rr, dr * 0.07, 0, TAU); ctx.fill();
      }
    }
    ctx.restore();
  }

  function poppy(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var n = 4 + Math.floor(r() * 3), back = Math.ceil(n / 2), front = n - back;
    var o = span(open, 0, 0.9);
    var blotch = mix(INK, c, 0.18);
    ctx.save();
    ctx.scale(1, 0.84);
    var fillIn = petalFill(ctx, 1, [0, css(blotch), 0.13, css(blotch), 0.24, css(mix(c, acc, 0.3)), 0.62, css(c), 1, css(lift(c, 0.3))]);
    for (var i = 0; i < back; i++) {
      var a = (back === 1 ? 0 : lerp(-62, 62, i / (back - 1))) + (r() - 0.5) * 10;
      ctx.save();
      ctx.rotate(a * PI / 180 * (0.25 + 0.75 * o));
      ctx.scale(0.62 + 0.38 * o, 0.6 + 0.4 * o);
      petalPath(ctx, 'broad', 1.02, 0.95, 0.06 * (r() - 0.5));
      shade(ctx, P, fillIn, 1, 0.95, 0, r);
      crinkle(ctx, P, r, 1, 0.95);
      ctx.restore();
    }
    if (o > 0.15) { // stamens and seed pod
      ctx.globalAlpha = c01((o - 0.15) * 2);
      ctx.strokeStyle = 'rgba(30,20,30,0.85)';
      ctx.lineWidth = 0.8 * P.px;
      ctx.beginPath();
      for (var s = 0; s < 22; s++) {
        var sa = (s / 22) * TAU, sr = 0.3 + r() * 0.06;
        ctx.moveTo(Math.cos(sa) * 0.12, -0.12 + Math.sin(sa) * 0.1);
        ctx.lineTo(Math.cos(sa) * sr, -0.12 + Math.sin(sa) * sr * 0.8);
      }
      ctx.stroke();
      ctx.fillStyle = '#2A1D2A';
      for (s = 0; s < 22; s++) {
        var ta = (s / 22) * TAU + 0.1, tr = 0.32;
        ctx.beginPath(); ctx.arc(Math.cos(ta) * tr, -0.12 + Math.sin(ta) * tr * 0.8, 0.03, 0, TAU); ctx.fill();
      }
      ctx.fillStyle = radial(ctx, -0.05, -0.2, 0.22, [0, '#E3E8C6', 0.6, '#A8B57E', 1, '#6F7F4C']);
      ctx.beginPath(); ctx.ellipse(0, -0.13, 0.16, 0.13, 0, 0, TAU); ctx.fill();
      ctx.strokeStyle = 'rgba(60,40,60,0.8)';
      ctx.lineWidth = 0.9 * P.px;
      ctx.beginPath();
      for (s = 0; s < 7; s++) {
        var pa = (s / 7) * TAU;
        ctx.moveTo(0, -0.15); ctx.lineTo(Math.cos(pa) * 0.13, -0.15 + Math.sin(pa) * 0.07);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    // Front petals: outer side of the cup, rising from below
    var fillOut = petalFill(ctx, 0.85, [0, css(mix(c, acc, 0.42)), 0.45, css(c), 1, css(lift(c, 0.22))]);
    for (var j = 0; j < front; j++) {
      var side = front === 1 ? 0 : (j === 0 ? -1 : 1), fa = side * (6 + 16 * o) + (r() - 0.5) * 6;
      ctx.save();
      ctx.translate(side * (0.1 + 0.12 * o), 0.42 + 0.1 * o);
      ctx.rotate(fa * PI / 180);
      ctx.scale(0.78 + 0.12 * o, 0.95 - 0.3 * o);
      petalPath(ctx, 'broad', 0.86, 0.9, 0.05 * (r() - 0.5));
      shade(ctx, P, fillOut, 0.86, 0.9, 0, r);
      crinkle(ctx, P, r, 0.86, 0.9);
      ctx.restore();
    }
    ctx.restore();
  }

  // Tissue-paper crinkles on poppy petals.
  function crinkle(ctx, P, r, L, W) {
    if (!P.detail) { r(); return; }
    ctx.lineWidth = 0.55 * P.px;
    for (var k = 0; k < 7; k++) {
      var x = (k / 6 - 0.5) * W * 1.3, wob = (r() - 0.5) * 0.08;
      ctx.strokeStyle = k % 2 ? 'rgba(255,255,255,0.22)' : css(P.acc, 0.14);
      ctx.beginPath();
      ctx.moveTo(x * 0.12, -L * 0.2);
      ctx.bezierCurveTo(x * 0.5 + wob, -L * 0.45, x * 0.8 - wob, -L * 0.7, x, -L * 0.93);
      ctx.stroke();
    }
  }

  function iris(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var o = span(open, 0, 0.9);
    var dark = lum(c) < 0.35;
    var stdFill = petalFill(ctx, 1, [0, css(mix(c, acc, 0.4)), 0.5, css(lift(c, dark ? 0.14 : 0.06)), 1, css(lift(c, dark ? 0.32 : 0.22))]);
    // Spathe (green bracts) at the base of the bloom
    ctx.save(); ctx.translate(0, 0.32);
    sepals(ctx, P, 2, 0.4 + 0.25 * (1 - o), 0.12, 'blue', r, 46);
    ctx.restore();
    // Standards: three narrow upright petals leaning together
    var stds = [[0, 1.0], [-1, 0.9], [1, 0.9]];
    for (var i = 0; i < 3; i++) {
      var s = stds[i];
      ctx.save();
      ctx.translate(s[0] * 0.06, 0);
      ctx.rotate(s[0] * (4 + 26 * o) * PI / 180);
      ctx.scale((s[0] ? 0.82 : 1) * (0.4 + 0.6 * o), 0.78 + 0.22 * o);
      petalPath(ctx, 'obovate', s[1], 0.46, 0.05 * (r() - 0.5));
      shade(ctx, P, s[0] ? stdFill : petalFill(ctx, 1, [0, css(mix(c, acc, 0.55)), 1, css(mix(c, acc, 0.15))]), s[1], 0.46, 3, r);
      ctx.restore();
    }
    // Falls: two arch outward and droop, the third hangs toward the viewer
    for (var j = 0; j < 3; j++) {
      var side = j === 2 ? 0 : (j ? 1 : -1);
      ctx.save();
      ctx.translate(0, 0.04);
      if (side) {
        ctx.scale(side, 1);
        ctx.rotate(-(1 - o) * 1.75);
        ctx.scale(0.62 + 0.38 * o, 0.3 + 0.7 * o);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(0.34, -0.16, 0.74, -0.08, 0.86, 0.26);
        ctx.quadraticCurveTo(0.95, 0.66 + (r() - 0.5) * 0.08, 0.62, 0.84);
        ctx.quadraticCurveTo(0.5, 0.88, 0.44, 0.74);
        ctx.bezierCurveTo(0.38, 0.5, 0.28, 0.24, 0.02, 0.12);
        ctx.closePath();
        shade(ctx, P, grad(ctx, 0, 0, 0.66, 0.84, [0, css(lift(c, 0.55)), 0.3, css(lift(c, 0.1)), 0.7, css(c), 1, css(mix(c, INK, 0.3))]), 0, 0, 0, r);
        if (o > 0.25) {
          ctx.globalAlpha = c01((o - 0.25) * 2);
          if (P.detail) {
            ctx.strokeStyle = 'rgba(255,255,255,0.5)';
            ctx.lineWidth = 0.6 * P.px;
            ctx.beginPath();
            for (var v = 0; v < 6; v++) {
              ctx.moveTo(0.06, 0.04);
              ctx.quadraticCurveTo(0.3, 0.02 + v * 0.04, 0.42 + v * 0.03, 0.12 + v * 0.07);
            }
            ctx.stroke();
          }
          for (var bd = 0; bd < 11; bd++) {
            var u = bd / 10;
            ctx.fillStyle = bd < 7 ? '#F2B53A' : '#F7D46A';
            ctx.beginPath(); ctx.arc(0.05 + u * 0.4, -0.03 - Math.sin(u * 2.4) * 0.06 + u * 0.06, 0.03 * (1 - u * 0.4), 0, TAU); ctx.fill();
          }
          ctx.globalAlpha = 1;
        }
      } else if (o > 0.3) {
        var fo = c01((o - 0.3) / 0.7);
        ctx.rotate(PI);
        ctx.scale(0.5 + 0.2 * fo, 0.55 * fo + 0.2);
        petalPath(ctx, 'broad', 0.82, 0.5, 0);
        shade(ctx, P, petalFill(ctx, 0.82, [0, css(lift(c, 0.45)), 0.35, css(c), 1, css(mix(c, INK, 0.3))]), 0.82, 0.5, 0, r);
        for (bd = 0; bd < 7; bd++) {
          ctx.fillStyle = '#F2B53A';
          ctx.beginPath(); ctx.arc(0, -0.05 - bd * 0.045, 0.035, 0, TAU); ctx.fill();
        }
      }
      ctx.restore();
    }
  }

  function rose(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var o = span(open, 0, 0.9);
    ctx.save();
    ctx.scale(1, 0.74);
    // Guard petals
    var gfill = petalFill(ctx, 1, [0, css(mix(c, acc, 0.4)), 0.5, css(c), 0.9, css(lift(c, 0.32)), 1, css(lift(c, 0.5))]);
    ring(ctx, P, {
      n: 6, L: 0.98, W: 0.8, ruf: 0.05, shape: 'cup', offset: r() * 60, jitter: 16,
      spread: 0.3 + 0.7 * o, sx: 0.62 + 0.38 * o, sy: 0.45 + 0.55 * o, fill: gfill, veins: 0
    }, r);
    // Cup interior
    var cupR = 0.66 * (0.6 + 0.4 * o);
    ctx.globalAlpha = P.glass ? P.alpha : 1;
    ctx.fillStyle = radial(ctx, 0, -0.1, cupR, [0, css(mix(c, acc, 0.75)), 0.6, css(mix(c, acc, 0.4)), 1, css(mix(c, acc, 0.15))]);
    ctx.beginPath(); ctx.ellipse(0, -0.1, cupR, cupR * 0.9, 0, 0, TAU); ctx.fill();
    ctx.globalAlpha = 1;
    // Spiral of cupped petals, innermost first so outer walls overlap them
    var N = 9, rot = r() * TAU;
    for (var k = N - 1; k >= 0; k--) {
      var f = k / (N - 1);
      var rad = cupR * lerp(0.22, 1.02, f);
      var mid = rot + k * 2.2 + PI * 0.5;
      var half = lerp(1.05, 1.5, f) * PI * 0.5;
      var cy = -0.1 - (1 - f) * 0.14;
      crescent(ctx, P, 0, cy, rad, mid - half, mid + half, lerp(0.35, 0.22, f), f);
    }
    // Tight swirl in the heart
    ctx.strokeStyle = css(mix(c, acc, 0.6), 0.7 * (P.glass ? 1 : P.alpha));
    ctx.lineWidth = 0.9 * P.px;
    ctx.beginPath();
    for (var t = 0; t < 1; t += 0.04) {
      var ang = rot + t * 9, rr = 0.02 + t * cupR * 0.25;
      var x = Math.cos(ang) * rr, y = -0.24 + Math.sin(ang) * rr * 0.8;
      if (t === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  // One cupped rose petal seen from outside: a crescent with a rolled rim.
  function crescent(ctx, P, x, y, rad, a0, a1, thick, f) {
    var c = P.c, acc = P.acc;
    ctx.beginPath();
    ctx.ellipse(x, y, rad, rad * 0.86, 0, a0, a1);
    ctx.ellipse(x, y - rad * thick, rad * 0.86, rad * 0.86 * 0.8, 0, a1, a0, true);
    ctx.closePath();
    ctx.globalAlpha = P.alpha;
    ctx.fillStyle = radial(ctx, x, y - rad * 0.4, rad * 1.25, [0, css(mix(c, acc, 0.62)), 0.5, css(mix(c, acc, 0.2 + 0.15 * (1 - f))), 0.82, css(c), 1, css(lift(c, 0.4))]);
    ctx.fill();
    // rolled rim highlight on the outer edge
    ctx.globalAlpha = P.glass ? 0.95 : 0.55 * P.alpha;
    ctx.strokeStyle = P.glass ? 'rgba(255,255,255,0.95)' : css(lift(c, 0.55));
    ctx.lineWidth = (P.glass ? 1.2 : 0.9) * P.px;
    ctx.beginPath();
    ctx.ellipse(x, y, rad, rad * 0.86, 0, a0 + 0.1, a1 - 0.1);
    ctx.stroke();
    ctx.globalAlpha = P.glass ? 0.6 : 0.3 * P.alpha;
    ctx.strokeStyle = P.edgeCol;
    ctx.lineWidth = 0.5 * P.px;
    ctx.beginPath();
    ctx.ellipse(x, y - rad * thick, rad * 0.86, rad * 0.86 * 0.8, 0, a0 + 0.15, a1 - 0.15);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  function zinnia(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var rings = [[16, 1, 0.3], [15, 0.84, 0.28], [13, 0.67, 0.26], [11, 0.5, 0.24]];
    ctx.save();
    ctx.scale(1, 0.62);
    for (var li = 0; li < rings.length; li++) {
      var s = rings[li];
      var o = span(open, li * 0.09, 0.6 + li * 0.1);
      if (li > 0) hollow(ctx, s[1] * 1.02, 0.14, 0);
      var fill = petalFill(ctx, s[1], [0, css(mix(c, acc, 0.48)), 0.42, css(c), 0.88, css(lift(c, 0.2)), 1, css(lift(c, 0.3))]);
      ring(ctx, P, {
        n: s[0], L: s[1], W: s[2], ruf: 0, shape: 'point', offset: li * 11 + r() * 20, jitter: 8,
        spread: 0.25 + 0.75 * o, sx: 0.5 + 0.5 * o, sy: 0.3 + 0.7 * o, fill: fill, veins: 1, y: -0.035 * li
      }, r);
    }
    ctx.restore();
    // Domed center: maroon florets with a ring of tiny golden stars
    var dO = 0.5 + 0.5 * span(open, 0.3, 1);
    ctx.save();
    ctx.translate(0, -0.12);
    ctx.scale(dO, dO * 0.78);
    ctx.fillStyle = radial(ctx, -0.06, -0.1, 0.34, [0, '#9A5A2E', 0.5, '#6B2E1A', 1, '#3A150D']);
    ctx.beginPath(); ctx.arc(0, 0, 0.27, 0, TAU); ctx.fill();
    ctx.fillStyle = '#F4C542';
    for (var i = 0; i < 14; i++) {
      var a = (i / 14) * TAU + 0.3;
      star(ctx, Math.cos(a) * 0.235, Math.sin(a) * 0.235, 0.032);
    }
    ctx.fillStyle = 'rgba(40,12,6,0.45)';
    for (i = 0; i < 16; i++) {
      var a2 = i * 2.39996, rr2 = 0.16 * Math.sqrt((i + 0.5) / 16);
      ctx.beginPath(); ctx.arc(Math.cos(a2) * rr2, Math.sin(a2) * rr2, 0.018, 0, TAU); ctx.fill();
    }
    ctx.fillStyle = radial(ctx, -0.07, -0.1, 0.2, [0, 'rgba(255,240,210,0.35)', 1, 'rgba(255,240,210,0)']);
    ctx.beginPath(); ctx.arc(0, 0, 0.27, 0, TAU); ctx.fill();
    ctx.restore();
  }

  function star(ctx, x, y, s) {
    ctx.beginPath();
    for (var i = 0; i < 10; i++) {
      var a = (i / 10) * TAU - PI / 2, rr = i % 2 ? s * 0.45 : s;
      ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    ctx.closePath();
    ctx.fill();
  }

  // A ring of petals like ring(), but shade() gets no veins so its glitter flecks and edge
  // follow the petal outline; fine veins are drawn afterwards and stay inside narrow rays
  // and pointed tepals. Extra fields: va (vein alpha), vw (vein bow), vt (vein tip spread).
  function whorl(ctx, P, R, r) {
    var list = [], i, k, p, a, q, n = R.veins;
    for (i = 0; i < R.n; i++) {
      a = (360 / R.n) * i + R.offset + (r() - 0.5) * R.jitter;
      a = ((a + 180) % 360 + 360) % 360 - 180;
      list.push({ a: a * PI / 180, L: R.L * (0.9 + r() * 0.18), W: R.W * (0.86 + r() * 0.24), ruf: R.ruf * (r() - 0.3), s: r() * 1e9 });
    }
    list.sort(function (u, v) { return Math.abs(u.a) - Math.abs(v.a); });
    for (i = 0; i < list.length; i++) {
      p = list[i];
      ctx.save();
      if (R.y) ctx.translate(0, R.y);
      ctx.rotate(p.a * R.spread);
      ctx.scale(R.sx, R.sy);
      petalPath(ctx, R.shape, p.L, p.W, p.ruf);
      shade(ctx, P, R.fill, p.L, p.W, 0, rng(p.s));
      if (P.detail && n) {
        ctx.globalAlpha = P.alpha * (P.glass ? 0.3 : R.va);
        ctx.strokeStyle = P.veinCol;
        ctx.lineWidth = 0.55 * P.px;
        ctx.beginPath();
        for (k = 0; k < n; k++) {
          q = n > 1 ? (k / (n - 1)) * 2 - 1 : 0;
          ctx.moveTo(q * p.W * 0.06, -p.L * 0.08);
          ctx.quadraticCurveTo(q * p.W * R.vw, -p.L * 0.45, q * p.W * R.vt, -p.L * 0.88);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }
  }

  // Daisy in three-quarter view: two staggered rings of narrow rays around a domed golden disc.
  function daisy(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var o1 = span(open, 0, 0.8), o2 = span(open, 0.05, 0.9), od = span(open, 0, 0.85);
    // Tiny heads get fewer, slightly wider rays so they stay bright instead of fuzzy.
    var small = 1 / P.px < 18, n = small ? 15 : 20, w = small ? 0.24 : 0.2, off = r() * 18;
    ctx.save();
    ctx.rotate((r() - 0.5) * 0.24);
    ctx.save();
    ctx.scale(1, 0.72);
    var fill = petalFill(ctx, 1, [0, css(mix(c, acc, 0.4)), 0.16, css(mix(c, acc, 0.14)), 0.55, css(c), 1, css(lift(c, 0.42))]);
    whorl(ctx, P, {
      n: n, L: 1, W: w, ruf: 0.03, shape: 'strap', offset: off, jitter: 7,
      spread: 0.12 + 0.88 * o1, sx: 0.5 + 0.5 * o1, sy: 0.38 + 0.62 * o1, fill: fill,
      veins: small ? 0 : 2, va: 0.2, vw: 0.22, vt: 0.2
    }, r);
    hollow(ctx, 0.8, 0.12 * P.alpha * o1, 0);
    whorl(ctx, P, {
      n: n - 1, L: 0.88, W: w * 0.95, ruf: 0.03, shape: 'strap', offset: off + 180 / n, jitter: 9,
      spread: 0.1 + 0.9 * o2, sx: 0.48 + 0.52 * o2, sy: 0.34 + 0.66 * o2, fill: fill,
      veins: small ? 0 : 2, va: 0.2, vw: 0.22, vt: 0.2, y: -0.02
    }, r);
    ctx.restore();
    // Domed disc: soft shadow under the dome, then the dome with a floret texture
    var dr = 0.28 * (0.55 + 0.45 * od), cy = -0.02, sh = P.alpha * o1;
    ctx.save();
    ctx.translate(0.02, cy + dr * 0.2);
    ctx.scale(1, 0.72);
    ctx.fillStyle = radial(ctx, 0, 0, dr * 1.6, [0, 'rgba(70,44,12,' + 0.34 * sh + ')', 0.6, 'rgba(70,44,12,' + 0.14 * sh + ')', 1, 'rgba(70,44,12,0)']);
    ctx.beginPath(); ctx.arc(0, 0, dr * 1.6, 0, TAU); ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.translate(0, cy);
    ctx.scale(1, 0.78);
    ctx.globalAlpha = P.glass ? Math.min(1, P.alpha + 0.3) : 1;
    var dg = ctx.createRadialGradient(-dr * 0.28, -dr * 0.45, 0, 0, -dr * 0.1, dr * 1.1);
    dg.addColorStop(0, '#FFF3A8'); dg.addColorStop(0.32, '#F8D24C'); dg.addColorStop(0.66, '#E4A321');
    dg.addColorStop(0.86, '#B97618'); dg.addColorStop(1, '#8A5412');
    ctx.fillStyle = dg;
    ctx.beginPath(); ctx.arc(0, 0, dr, 0, TAU); ctx.fill();
    if (P.detail) {
      var N = 44, rot = r() * TAU, i, a, u, x, y, s;
      ctx.fillStyle = 'rgba(126,72,8,0.5)';
      ctx.beginPath();
      for (i = 0; i < N; i++) {
        a = rot + i * 2.39996; u = Math.sqrt((i + 0.5) / N) * 0.9;
        x = Math.cos(a) * u * dr; y = Math.sin(a) * u * dr - (1 - u * u) * dr * 0.22; s = dr * 0.065 * (1 - 0.35 * u);
        ctx.moveTo(x + s, y); ctx.arc(x, y, s, 0, TAU);
      }
      ctx.fill();
      ctx.fillStyle = 'rgba(255,248,200,0.55)';
      ctx.beginPath();
      for (i = 0; i < N; i++) {
        a = rot + i * 2.39996; u = Math.sqrt((i + 0.5) / N) * 0.9;
        x = Math.cos(a) * u * dr; y = Math.sin(a) * u * dr - (1 - u * u) * dr * 0.22; s = dr * 0.03 * (1 - 0.35 * u);
        if (y < dr * 0.2) { ctx.moveTo(x - s * 0.6 + s, y - s * 0.8); ctx.arc(x - s * 0.6, y - s * 0.8, s, 0, TAU); }
      }
      ctx.fill();
      ctx.strokeStyle = 'rgba(110,60,8,0.45)';
      ctx.lineWidth = 0.8 * P.px;
      ctx.beginPath(); ctx.arc(0, 0, dr * 0.98, PI * 0.12, PI * 0.88); ctx.stroke();
    }
    if (P.glass) {
      ctx.globalAlpha = 0.85;
      ctx.strokeStyle = 'rgba(255,255,255,1)';
      ctx.lineWidth = 1.1 * P.px;
      ctx.beginPath(); ctx.arc(0, 0, dr, 0, TAU); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
    ctx.restore();
  }

  // Tulip in three-quarter view, seen from the side and a little above: a cup of six smooth
  // tepals in two whorls of three. Each tepal stands at its own angle around the cup; that angle
  // places its base on the receptacle, sets how far it leans out as the cup opens, how much of
  // its width faces us, and whether we see its outer face (front half of the cup) or the inside
  // of the cup (back half). The outer whorl opens first and leans out further; the inner whorl
  // stays a little closed so the bloom keeps its cup shape and never goes flat.
  function tulip(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open, i, k, T;
    var oo = span(open, 0, 0.72), oi = span(open, 0.16, 1);
    var bud = 1 - span(open, 0.04, 0.7);               // green tinge of the young bud
    var turn = (r() - 0.5) * 0.7, EL = 0.5, ce = Math.cos(EL), se = Math.sin(EL);
    var rb = lerp(0.1, 0.2, oo);                       // receptacle radius: tepal bases sit on it
    var budG = [150, 182, 120], warmT = [206, 122, 88]; // a fresh, light bud green
    var lc = lum(c), deep = 0.26 + 0.16 * (1 - lc);
    // Outer face: darker and warmer at the base, the shade itself through the middle, a light
    // and slightly see-through tip. Inner face (inside the cup): deep at the bottom, satin above.
    // The bud is green at the base and blushes with color from the tips down. While it is a bud
    // the warm, deep base shading eases off, so pink and green never mix into a dried khaki.
    // Green stays low on the bud and the color stays nearly pure above it; the band between is
    // lifted toward light, because pink and green mixed at full strength turn grey.
    var outBase = mix(mix(mix(c, acc, deep * (1 - 0.6 * bud)), warmT, 0.16 * (1 - bud)), budG, 0.72 * bud);
    var outMid = mix(c, budG, 0.14 * bud), outTip = mix(lift(c, 0.4), budG, 0.06 * bud);
    var outBand = mix(mix(outBase, outMid, 0.6), WHITE, 0.22 * bud);
    var inBase = mix(mix(c, INK, 0.5), budG, 0.5 * bud), inMid = mix(mix(c, acc, 0.22), budG, 0.4 * bud);
    var tips = P.alpha < 0.95 ? 0.7 : 0.84;
    var list = [];
    for (i = 0; i < 6; i++) {
      var outer = i % 2 === 0, o = outer ? oo : oi;
      var th = turn + i * PI / 3 + (r() - 0.5) * 0.14;
      var cs = Math.cos(th), sn = Math.sin(th);
      var L = (outer ? 1.62 : 1.88) * lerp(0.6, 1, o) * (0.95 + r() * 0.08);
      var Wd = (outer ? 1.02 : 0.9) * lerp(0.56, 1, o) * (0.94 + r() * 0.1);
      var phi = lerp(-0.1, outer ? 0.38 : 0.22, o) + (r() - 0.5) * 0.05;
      var reach = rb + L * Math.sin(phi);
      var bx = sn * rb, by = cs * rb * se;
      var tx = sn * reach, ty = -L * Math.cos(phi) * ce + cs * reach * se;
      list.push({
        outer: outer, cs: cs, sn: sn, bx: bx, by: by, a: Math.atan2(tx - bx, by - ty),
        L: Math.sqrt((tx - bx) * (tx - bx) + (ty - by) * (ty - by)),
        W: Wd * (0.36 + 0.64 * Math.abs(cs)), ruf: (r() - 0.5) * 0.05, s: (r() * 1e9) | 0
      });
    }
    var back = list.filter(function (q) { return q.cs < 0; }), front = list.filter(function (q) { return q.cs >= 0; });
    var byDepth = function (p, q) { return p.cs - q.cs; };
    back.sort(byDepth); front.sort(byDepth);
    // Receptacle: the stem swells a little where the cup sits on it.
    var g = P.greens || GREENS.sage;
    ctx.fillStyle = radial(ctx, -0.05, -0.02, 0.24, [0, css(g[0]), 0.6, css(g[1]), 1, css(g[2])]);
    ctx.beginPath(); ctx.ellipse(0, 0.02, 0.15 + 0.04 * oo, 0.12, 0, 0, TAU); ctx.fill();
    // Back half of the cup: outer whorl first, the inner whorl nests in front of it.
    for (k = 0; k < 2; k++) {
      for (i = 0; i < back.length; i++) {
        T = back[i];
        if (T.outer === !k) tepal(ctx, P, T, false, [0, css(inBase), 0.34, css(inMid), 0.7, css(c), 1, css(lift(c, 0.44), tips)]);
      }
    }
    // The hollow of the cup: shade pooled low between the back tepals, darkest at the base.
    // Pale shades get a warm olive depth instead of grey, like the throat of a white tulip.
    // Clear shades show the hollow through their front tepals, so it stays lighter there.
    var cy = -lerp(0.6, 1.32, oo), cw = lerp(0.2, 0.8, oo), sh = P.alpha * lerp(0.35, 1, oo) * (P.alpha < 0.95 ? 0.5 : 1);
    var dk = lc > 0.82 ? mix(c, [96, 84, 46], 0.55) : mix(c, INK, 0.62);
    ctx.save();
    ctx.translate(0, cy);
    ctx.scale(1, 0.46);
    ctx.fillStyle = radial(ctx, 0, 0.16, cw, [0, css(dk, 0.62 * sh), 0.42, css(mix(c, acc, 0.5), 0.32 * sh), 1, css(acc, 0)]);
    ctx.beginPath(); ctx.arc(0, 0.16, cw, 0, TAU); ctx.fill();
    ctx.restore();
    // Front half: inner whorl first, the outer whorl wraps around it.
    for (k = 0; k < 2; k++) {
      for (i = 0; i < front.length; i++) {
        T = front[i];
        if (T.outer === !!k) tepal(ctx, P, T, true, [0, css(outBase), 0.36, css(outBand), 0.66, css(outMid), 1, css(outTip, tips)]);
      }
    }
  }

  // One tulip tepal. outside: true for the outer face (front of the cup), false for the inside.
  function tepal(ctx, P, T, outside, stops) {
    var c = P.c, acc = P.acc, L = T.L, W = T.W, rr = rng(T.s), clear = P.alpha < 0.95, v, q;
    ctx.save();
    ctx.translate(T.bx, T.by);
    ctx.rotate(T.a);
    petalPath(ctx, 'tulip', L, W, T.ruf);
    ctx.globalAlpha = P.alpha;
    ctx.fillStyle = petalFill(ctx, L, stops);
    ctx.fill();
    // Rounded body across the width: the outer face is convex (edges fall into shade, a sheen
    // left of the middle toward the light); the inside is concave (the middle sits deeper).
    var sd = 0.5 + 0.5 * c01(T.sn * 0.9 + 0.1);
    ctx.fillStyle = outside
      ? grad(ctx, -W * 0.56, 0, W * 0.56, 0, [0, css(acc, 0.16 + 0.1 * (1 - sd)), 0.26, css(WHITE, 0), 0.4, css(WHITE, 0.2 * (1.2 - sd)), 0.56, css(WHITE, 0), 1, css(acc, 0.2 + 0.14 * sd)])
      : grad(ctx, -W * 0.56, 0, W * 0.56, 0, [0, css(WHITE, 0.16), 0.5, css(acc, 0.12), 1, css(WHITE, 0.1)]);
    ctx.fill();
    if (P.detail) { // faint veins running up the tepal
      ctx.globalAlpha = P.alpha * (outside ? 0.08 : 0.06);
      ctx.strokeStyle = P.veinCol;
      ctx.lineWidth = 0.5 * P.px;
      ctx.beginPath();
      for (v = 0; v < 5; v++) {
        q = (v / 4) * 2 - 1;
        ctx.moveTo(q * W * 0.05, -L * 0.07);
        ctx.quadraticCurveTo(q * W * 0.36, -L * 0.48, q * W * (0.2 + rr() * 0.05), -L * (0.84 + rr() * 0.06));
      }
      ctx.stroke();
    }
    petalPath(ctx, 'tulip', L, W, T.ruf); // the outline again: veins replaced the current path
    if (P.glitter && P.detail) {
      ctx.save();
      ctx.clip();
      ctx.globalAlpha = 1;
      for (v = 0; v < 9; v++) {
        ctx.fillStyle = rr() < 0.6 ? 'rgba(255,255,255,0.85)' : css(mix(c, WHITE, 0.6), 0.9);
        var gs = (0.5 + rr() * 0.9) * P.px;
        ctx.fillRect((rr() - 0.5) * W * 0.9, -L * (0.15 + rr() * 0.8), gs, gs);
      }
      ctx.restore();
    }
    // Edge: clear and sheer shades get a bright rim so they read as translucent, not faded.
    ctx.globalAlpha = clear ? 0.22 : P.alpha * 0.32;
    ctx.strokeStyle = css(mix(c, acc, 0.6));
    ctx.lineWidth = 0.6 * P.px;
    ctx.stroke();
    if (clear) {
      ctx.globalAlpha = outside ? 0.85 : 0.5;
      ctx.strokeStyle = css(lift(c, 0.75));
      ctx.lineWidth = 1.1 * P.px;
      ctx.stroke();
    } else if (P.detail) {
      ctx.globalAlpha = P.alpha * 0.4;
      ctx.strokeStyle = css(lift(c, 0.6));
      ctx.lineWidth = 0.7 * P.px;
      ctx.beginPath();
      ctx.moveTo(-W * 0.5, -L * 0.8); ctx.bezierCurveTo(-W * 0.4, -L * 0.92, -W * 0.2, -L * 0.96, 0, -L);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  // Papery spathe: a thin dry bract pointing up from the neck (base at 0,0).
  function spathe(ctx, P, len, wid) {
    ctx.beginPath();
    ctx.moveTo(-wid * 0.5, 0);
    ctx.bezierCurveTo(-wid * 0.62, -len * 0.4, -wid * 0.3, -len * 0.75, wid * 0.08, -len);
    ctx.bezierCurveTo(wid * 0.1, -len * 0.7, wid * 0.55, -len * 0.4, wid * 0.5, 0);
    ctx.closePath();
    ctx.fillStyle = grad(ctx, 0, 0, 0, -len, [0, '#8F7A52', 0.35, '#CDB688', 0.8, '#E6D6B2', 1, '#D9C59C']);
    ctx.fill();
    if (P.detail) {
      ctx.strokeStyle = 'rgba(118,94,58,0.35)';
      ctx.lineWidth = 0.5 * P.px;
      ctx.beginPath();
      for (var k = -1; k <= 1; k++) {
        ctx.moveTo(k * wid * 0.25, -len * 0.04);
        ctx.quadraticCurveTo(k * wid * 0.18, -len * 0.5, wid * 0.04 + k * wid * 0.05, -len * 0.9);
      }
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(120,96,60,0.45)';
    ctx.lineWidth = 0.5 * P.px;
    ctx.beginPath();
    ctx.moveTo(-wid * 0.5, 0);
    ctx.bezierCurveTo(-wid * 0.62, -len * 0.4, -wid * 0.3, -len * 0.75, wid * 0.08, -len);
    ctx.stroke();
  }

  // Daffodil in three-quarter view: six pointed tepals in a flat star behind a flared trumpet
  // with a frilled rim. Opening: a sheathed bud splits, the tepals spread, the trumpet lengthens.
  function daffodil(ctx, P, r) {
    var c = P.c, acc = P.acc, open = P.open;
    var ob = span(open, 0, 0.35), op = span(open, 0.08, 0.72), ot = span(open, 0.3, 1);
    var side = r() < 0.5 ? -1 : 1, lean = side * (0.12 + r() * 0.12), off = (r() - 0.5) * 24;
    var glow = mix([243, 172, 88], c, 0.22), cup = mix(mix(c, acc, 0.3), glow, 0.16), i, k;
    ctx.save();
    // Dry bract at the neck, behind the flower, leaning away as the bud splits
    ctx.save();
    ctx.translate(-side * 0.04, 0.12);
    ctx.rotate(-side * lerp(0.08, 0.62, op));
    ctx.scale(-side, 1);
    ctx.globalAlpha = P.glass ? 0.7 : 1;
    spathe(ctx, P, lerp(0.92, 1.2, op), lerp(0.36, 0.2, op));
    ctx.globalAlpha = 1;
    ctx.restore();
    // The open head nods a little off the neck, toward the side it leans.
    ctx.translate(side * 0.1 * op, -0.16 * op);
    ctx.rotate(lean * op);
    // Perianth: three outer tepals, then three inner ones on top
    ctx.save();
    ctx.scale(1, lerp(1, 0.74, op));
    var tf = petalFill(ctx, 1, [0, css(mix(c, acc, 0.4)), 0.28, css(mix(c, acc, 0.12)), 0.7, css(c), 1, css(lift(c, 0.36))]);
    var sp = lerp(0.05, 1, op), sx = lerp(0.42, 1, op);
    whorl(ctx, P, { n: 3, L: 1, W: 0.66, ruf: 0.06, shape: 'tepal', offset: off, jitter: 8, spread: sp, sx: sx, sy: 1, fill: tf, veins: 5, va: 0.12, vw: 0.44, vt: 0.1 }, r);
    whorl(ctx, P, { n: 3, L: 0.92, W: 0.62, ruf: 0.06, shape: 'tepal', offset: off + 60, jitter: 8, spread: sp, sx: sx * 0.96, sy: 1, fill: tf, veins: 5, va: 0.12, vw: 0.44, vt: 0.1 }, r);
    ctx.restore();
    // Sheath around the bud, splitting in two and falling away
    if (ob < 1) {
      for (k = -1; k <= 1; k += 2) {
        ctx.save();
        ctx.translate(k * 0.02, 0.1);
        ctx.rotate(k * ob * 0.7);
        ctx.globalAlpha = (1 - ob) * (1 - ob);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(k * 0.22, -0.02);
        ctx.quadraticCurveTo(k * 0.26, -0.42, k * 0.02, -0.8);
        ctx.quadraticCurveTo(k * 0.05, -0.4, 0, 0);
        ctx.closePath();
        ctx.fillStyle = grad(ctx, 0, 0, 0, -0.8, [0, '#8F7A52', 0.4, '#CDB688', 1, '#E6D6B2']);
        ctx.fill();
        ctx.strokeStyle = 'rgba(120,96,60,0.5)';
        ctx.lineWidth = 0.5 * P.px;
        ctx.stroke();
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    }
    // Trumpet (corona): rim ellipse toward the viewer with the cup body below it
    if (ot > 0.01) {
      ctx.save();
      var rb = lerp(0.14, 0.2, ot), rr = lerp(0.18, 0.44, ot), d = lerp(0.05, 0.5, ot), ry = rr * 0.6, rby = rb * 0.6;
      ctx.save(); ctx.scale(1, 0.74); hollow(ctx, 0.3 + 0.25 * ot, 0.22 * P.alpha, 0.08); ctx.restore();
      var N = P.detail ? 40 : 20, ph = r() * TAU, rim = [];
      for (i = 0; i <= N; i++) {
        var th = (i / N) * TAU, f = 1 + 0.055 * ot * Math.sin(th * 11 + ph) + 0.02 * Math.sin(th * 23 + ph * 2);
        rim.push([Math.cos(th) * rr * f, -d + Math.sin(th) * ry * f]);
      }
      var h = N / 2;
      // outer wall: base to the front half of the rim
      ctx.beginPath();
      ctx.moveTo(-rb, 0);
      ctx.bezierCurveTo(-rb * 1.08, -d * 0.45, -rr * 0.66, -d * 0.7, rim[h][0], rim[h][1]);
      for (i = h - 1; i >= 0; i--) ctx.lineTo(rim[i][0], rim[i][1]);
      ctx.bezierCurveTo(rr * 0.66, -d * 0.7, rb * 1.08, -d * 0.45, rb, 0);
      ctx.ellipse(0, 0, rb, rby, 0, 0, PI);
      ctx.closePath();
      ctx.globalAlpha = P.alpha;
      ctx.fillStyle = grad(ctx, 0, rby, 0, -d, [0, css(mix(cup, acc, 0.24)), 0.45, css(cup), 0.82, css(lift(cup, 0.24)), 1, css(lift(cup, 0.4))]);
      ctx.fill();
      ctx.fillStyle = grad(ctx, -rr, 0, rr, 0, [0, css(acc, 0.1), 0.18, 'rgba(255,255,255,0.12)', 0.34, 'rgba(255,255,255,0.32)', 0.52, 'rgba(255,255,255,0)', 0.72, css(acc, 0), 1, css(acc, 0.28)]);
      ctx.fill();
      ctx.globalAlpha = P.glass ? 0.9 : P.alpha * 0.4;
      ctx.strokeStyle = P.edgeCol;
      ctx.lineWidth = (P.glass ? 1.1 : 0.55) * P.px;
      ctx.stroke();
      if (P.detail) { // faint ribs running up the corona
        ctx.globalAlpha = P.alpha;
        ctx.strokeStyle = css(mix(cup, acc, 0.45), 0.16);
        ctx.lineWidth = 0.5 * P.px;
        ctx.beginPath();
        for (i = -2; i <= 2; i++) {
          var q = rim[Math.round(h / 2 - i * h / 5.5)];
          ctx.moveTo(i * rb * 0.36, rby * 0.9 - Math.abs(i) * 0.02);
          ctx.quadraticCurveTo(i * rb * 0.5, -d * 0.5, q[0] * 0.92, q[1]);
        }
        ctx.stroke();
      }
      // throat with a warm golden glow
      ctx.beginPath();
      ctx.moveTo(rim[0][0], rim[0][1]);
      for (i = 1; i < N; i++) ctx.lineTo(rim[i][0], rim[i][1]);
      ctx.closePath();
      var tg = ctx.createRadialGradient(0, -d + ry * 0.45, 0, 0, -d + ry * 0.1, rr * 1.05);
      tg.addColorStop(0, css(mix(glow, acc, 0.5))); tg.addColorStop(0.3, css(glow));
      tg.addColorStop(0.72, css(mix(cup, glow, 0.35))); tg.addColorStop(1, css(lift(cup, 0.25)));
      ctx.globalAlpha = P.alpha;
      ctx.fillStyle = tg;
      ctx.fill();
      if (P.detail) {
        ctx.strokeStyle = css(mix(glow, acc, 0.3), 0.22);
        ctx.lineWidth = 0.55 * P.px;
        ctx.beginPath();
        for (i = h + 2; i < N - 1; i += 2) { ctx.moveTo(0, -d + ry * 0.4); ctx.lineTo(rim[i][0] * 0.94, rim[i][1] * 0.98 - d * 0.02); }
        ctx.stroke();
        if (ot > 0.5) { // stamens peeking from the throat
          ctx.globalAlpha = P.alpha * c01((ot - 0.5) * 3);
          ctx.fillStyle = '#F6DF86';
          for (i = -1; i <= 1; i++) { ctx.beginPath(); ctx.ellipse(i * 0.045, -d + ry * 0.32 - Math.abs(i) * 0.02, 0.028, 0.02, 0, 0, TAU); ctx.fill(); }
        }
      }
      // rolled, frilled rim: light lip in front, finer edge behind
      ctx.lineCap = 'round';
      ctx.globalAlpha = P.glass ? 0.95 : 0.85 * P.alpha;
      ctx.strokeStyle = P.glass ? 'rgba(255,255,255,1)' : css(lift(cup, 0.45));
      ctx.lineWidth = (P.glass ? 1.2 : 0.8) * P.px;
      ctx.beginPath();
      ctx.moveTo(rim[h][0], rim[h][1]);
      for (i = h + 1; i <= N; i++) ctx.lineTo(rim[i][0], rim[i][1]);
      ctx.stroke();
      ctx.lineWidth = Math.max(1.3 * P.px, 0.035);
      ctx.beginPath();
      ctx.moveTo(rim[0][0], rim[0][1]);
      for (i = 1; i <= h; i++) ctx.lineTo(rim[i][0], rim[i][1]);
      ctx.stroke();
      if (P.detail) {
        ctx.globalAlpha = 0.5 * P.alpha;
        ctx.strokeStyle = css(mix(cup, acc, 0.5));
        ctx.lineWidth = 0.6 * P.px;
        ctx.beginPath();
        ctx.moveTo(rim[1][0] * 0.97, rim[1][1] + 0.03);
        for (i = 2; i < h; i++) ctx.lineTo(rim[i][0] * 0.97, rim[i][1] + 0.03);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }
    ctx.restore();
  }

  /* ---------- stem and leaves ---------- */

  // straight (0..1, default 1) scales the random bends: the tulip stem stands nearly upright.
  function stemCurve(L, r, straight) {
    var k = straight == null ? 1 : straight;
    var b1 = (r() - 0.5) * 0.16 * L * k, b2 = (r() - 0.5) * 0.12 * L * k, tx = (r() - 0.5) * 0.07 * L * k;
    return [0, 0, b1, -0.35 * L, b2, -0.7 * L, tx, -L];
  }
  function bez(p, t) {
    var u = 1 - t;
    return [
      u * u * u * p[0] + 3 * u * u * t * p[2] + 3 * u * t * t * p[4] + t * t * t * p[6],
      u * u * u * p[1] + 3 * u * u * t * p[3] + 3 * u * t * t * p[5] + t * t * t * p[7]
    ];
  }

  function drawStem(ctx, curve, prog, w0, greens, hairy) {
    var N = 14, L = [], Rt = [];
    for (var i = 0; i <= N; i++) {
      var t = (i / N) * prog, p = bez(curve, t), q = bez(curve, Math.min(1, t + 0.01));
      var dx = q[0] - p[0], dy = q[1] - p[1], len = Math.sqrt(dx * dx + dy * dy) || 1;
      var w = w0 * lerp(1, 0.62, t) * 0.5;
      L.push([p[0] - dy / len * -w, p[1] + dx / len * -w]);
      Rt.push([p[0] + dy / len * -w, p[1] - dx / len * -w]);
    }
    ctx.beginPath();
    ctx.moveTo(L[0][0], L[0][1]);
    for (i = 1; i <= N; i++) ctx.lineTo(L[i][0], L[i][1]);
    for (i = N; i >= 0; i--) ctx.lineTo(Rt[i][0], Rt[i][1]);
    ctx.closePath();
    ctx.fillStyle = css(greens[2]);
    ctx.fill();
    // light running down one side gives the stem a round body
    ctx.strokeStyle = css(greens[0], 0.75);
    ctx.lineWidth = Math.max(0.5, w0 * 0.28);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (i = 0; i <= N; i++) {
      var m = [lerp(L[i][0], Rt[i][0], 0.68), lerp(L[i][1], Rt[i][1], 0.68)];
      if (i === 0) ctx.moveTo(m[0], m[1]); else ctx.lineTo(m[0], m[1]);
    }
    ctx.stroke();
    if (hairy && w0 > 1.6) {
      ctx.strokeStyle = css(greens[0], 0.5);
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (i = 2; i < N; i += 1) {
        ctx.moveTo(L[i][0], L[i][1]); ctx.lineTo(L[i][0] - w0 * 0.5, L[i][1] - w0 * 0.3);
        ctx.moveTo(Rt[i][0], Rt[i][1]); ctx.lineTo(Rt[i][0] + w0 * 0.5, Rt[i][1] - w0 * 0.3);
      }
      ctx.stroke();
    }
    return bez(curve, prog);
  }

  // A leaf blade along +x. teeth/depth make it lobed or serrated; unfurl 0..1 curls it open.
  function leafBlade(ctx, len, wid, teeth, depth, curl, unfurl, greens, detail) {
    var N = 22, top = [], bot = [];
    for (var i = 0; i <= N; i++) {
      var u = i / N, x = u * len, mid = -curl * len * u * u;
      var hw = wid * Math.pow(Math.sin(PI * Math.pow(u, 0.75)), 0.85);
      if (teeth) hw *= 1 - depth * (0.5 - 0.5 * Math.cos(u * teeth * TAU));
      top.push([x, mid - hw * unfurl]);
      bot.push([x, mid + hw * (0.15 + 0.85 * unfurl * unfurl)]);
    }
    ctx.beginPath();
    ctx.moveTo(0, 0);
    for (i = 1; i <= N; i++) ctx.lineTo(top[i][0], top[i][1]);
    for (i = N; i >= 0; i--) ctx.lineTo(bot[i][0], bot[i][1]);
    ctx.closePath();
    ctx.fillStyle = grad(ctx, 0, -wid, 0, wid, [0, css(greens[0]), 0.5, css(greens[1]), 1, css(greens[2])]);
    ctx.fill();
    // underside half slightly darker, like the reference leaves
    ctx.beginPath();
    ctx.moveTo(0, 0);
    for (i = 1; i <= N; i++) ctx.lineTo(top[i][0], (top[i][1] + bot[i][1]) / 2);
    for (i = N; i >= 0; i--) ctx.lineTo(bot[i][0], bot[i][1]);
    ctx.closePath();
    ctx.fillStyle = 'rgba(20,45,22,0.18)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(40,74,42,0.55)';
    ctx.lineWidth = Math.max(0.5, wid * 0.08);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    for (i = 1; i < N; i++) ctx.lineTo(top[i][0], (top[i][1] + bot[i][1]) / 2);
    ctx.stroke();
    if (detail) {
      ctx.lineWidth = Math.max(0.4, wid * 0.04);
      ctx.strokeStyle = 'rgba(40,74,42,0.3)';
      ctx.beginPath();
      for (i = 3; i < N - 2; i += 3) {
        var mx = top[i][0], my = (top[i][1] + bot[i][1]) / 2;
        ctx.moveTo(mx, my); ctx.lineTo(top[i + 2][0], lerp(my, top[i + 2][1], 0.8));
        ctx.moveTo(mx, my); ctx.lineTo(bot[i + 2][0], lerp(my, bot[i + 2][1], 0.8));
      }
      ctx.stroke();
    }
  }

  function featherLeaf(ctx, len, wid, unfurl, greens) {
    ctx.strokeStyle = css(greens[1]);
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(0.6, wid * 0.12);
    ctx.beginPath();
    var pts = [];
    for (var i = 0; i <= 10; i++) {
      var u = i / 10, x = u * len, y = -0.25 * len * u * u;
      pts.push([x, y]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.lineWidth = Math.max(0.45, wid * 0.08);
    ctx.strokeStyle = css(greens[0]);
    ctx.beginPath();
    for (i = 2; i <= 9; i++) {
      var p = pts[i], l = wid * (1.4 - i * 0.09) * unfurl;
      ctx.moveTo(p[0], p[1]); ctx.quadraticCurveTo(p[0] + l * 0.3, p[1] - l * 0.6, p[0] + l * 0.7, p[1] - l * 1.1);
      ctx.moveTo(p[0], p[1]); ctx.quadraticCurveTo(p[0] + l * 0.5, p[1] + l * 0.2, p[0] + l * 1.0, p[1] + l * 0.4);
    }
    ctx.stroke();
  }

  function leaves(ctx, key, T, S, g, curve, greens, detail, r) {
    var spec = T.leaf, type = spec[0];
    if (type === 'sword' || type === 'strap') return; // iris and tulip leaves are drawn at the base
    var at = [0.3, 0.52];
    for (var i = 0; i < 2; i++) {
      var p = span(g, 0.22 + i * 0.08, 0.52 + i * 0.08);
      var side = i ? 1 : -1, k = (i ? 0.85 : 1) * (0.9 + r() * 0.2), tilt = 0.35 + r() * 0.25;
      if (p <= 0) continue;
      var pt = bez(curve, at[i]);
      ctx.save();
      ctx.translate(pt[0], pt[1]);
      ctx.scale(side, 1);
      ctx.rotate(-tilt);
      ctx.scale(p, p);
      if (type === 'feather') {
        featherLeaf(ctx, spec[1] * S * k, spec[2] * S, p, greens);
      } else if (type === 'rose') { // three serrated leaflets on a stalk
        var ll = spec[1] * S * k, lw = spec[2] * S;
        ctx.strokeStyle = css(greens[2]); ctx.lineWidth = Math.max(0.6, lw * 0.12);
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(ll * 1.3, -ll * 0.25); ctx.stroke();
        var lf = [[ll * 1.25, -ll * 0.24, -0.2], [ll * 0.7, -ll * 0.13, -0.95], [ll * 0.72, -ll * 0.14, 0.75]];
        for (var j = 0; j < 3; j++) {
          ctx.save(); ctx.translate(lf[j][0], lf[j][1]); ctx.rotate(lf[j][2]);
          leafBlade(ctx, ll * (j ? 0.8 : 1), lw, spec[3], spec[4], spec[5], p, greens, detail);
          ctx.restore();
        }
      } else {
        leafBlade(ctx, spec[1] * S * k, spec[2] * S, spec[3], spec[4], spec[5], p, greens, detail);
        if (key === 'peony' || key === 'peony-bud') { // second lobe gives the divided peony leaf
          ctx.rotate(0.55);
          leafBlade(ctx, spec[1] * S * k * 0.62, spec[2] * S * 0.8, 2, 0.3, spec[5], p, greens, false);
        }
      }
      ctx.restore();
    }
  }

  // Tulip leaves: two broad straps rising from the base and arching outward, folded along the
  // midrib so the upper face catches the light. unfurl 0..1 lengthens, widens and arches them.
  function strapLeaf(ctx, S, side, k, p, greens, detail) {
    var len = S * 0.5 * k * (0.42 + 0.58 * p), a = p * p, wmax = S * 0.068 * k * (0.5 + 0.5 * p);
    var sp = [0, 0, side * S * 0.012, -len * 0.45, side * S * (0.02 + 0.11 * a) * k, -len * (0.92 + 0.1 * a),
      side * S * (0.03 + 0.21 * a) * k, -len * (1 - 0.3 * a)];
    var N = 20, top = [], bot = [], mid = [], i;
    for (i = 0; i <= N; i++) {
      var u = i / N, pt = bez(sp, u), nx = bez(sp, Math.min(1, u + 0.02)), pv = bez(sp, Math.max(0, u - 0.02));
      var dx = nx[0] - pv[0], dy = nx[1] - pv[1], dl = Math.sqrt(dx * dx + dy * dy) || 1;
      var n = [side * dy / dl, -side * dx / dl];
      var hw = wmax * Math.pow(1 - u, 0.7) * (0.5 + 0.5 * Math.sin(Math.min(1, u / 0.3) * PI / 2));
      var fold = 0.5 + 0.3 * u; // the far half shows less of itself toward the tip
      top.push([pt[0] + n[0] * hw, pt[1] + n[1] * hw]);
      bot.push([pt[0] - n[0] * hw * fold, pt[1] - n[1] * hw * fold]);
      mid.push(pt);
    }
    var tip = mid[N];
    // far half (darker, turned away)
    ctx.beginPath();
    ctx.moveTo(mid[0][0], mid[0][1]);
    for (i = 1; i <= N; i++) ctx.lineTo(bot[i][0], bot[i][1]);
    for (i = N; i >= 0; i--) ctx.lineTo(mid[i][0], mid[i][1]);
    ctx.closePath();
    ctx.fillStyle = grad(ctx, 0, 0, tip[0], tip[1], [0, css(greens[2]), 0.6, css(mix(greens[2], greens[1], 0.5)), 1, css(greens[2])]);
    ctx.fill();
    // near half (the lit upper face)
    ctx.beginPath();
    ctx.moveTo(mid[0][0], mid[0][1]);
    for (i = 1; i <= N; i++) ctx.lineTo(top[i][0], top[i][1]);
    for (i = N; i >= 0; i--) ctx.lineTo(mid[i][0], mid[i][1]);
    ctx.closePath();
    ctx.fillStyle = grad(ctx, 0, 0, tip[0], tip[1], [0, css(greens[1]), 0.45, css(greens[0]), 0.8, css(mix(greens[0], greens[1], 0.5)), 1, css(greens[1])]);
    ctx.fill();
    // a soft bloom of light along the upper face
    ctx.fillStyle = css(WHITE, 0.08);
    ctx.fill();
    // midrib crease and a fine edge
    ctx.lineCap = 'round';
    ctx.strokeStyle = css(greens[2], 0.55);
    ctx.lineWidth = Math.max(0.5, wmax * 0.07);
    ctx.beginPath();
    ctx.moveTo(mid[0][0], mid[0][1]);
    for (i = 1; i <= N; i++) ctx.lineTo(mid[i][0], mid[i][1]);
    ctx.stroke();
    if (detail) { // parallel veins on the lit face
      ctx.strokeStyle = css(greens[2], 0.16);
      ctx.lineWidth = Math.max(0.4, wmax * 0.035);
      ctx.beginPath();
      for (var v = 1; v <= 2; v++) {
        var f = v / 3;
        ctx.moveTo(lerp(mid[1][0], top[1][0], f), lerp(mid[1][1], top[1][1], f));
        for (i = 2; i < N; i++) ctx.lineTo(lerp(mid[i][0], top[i][0], f), lerp(mid[i][1], top[i][1], f));
      }
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(40,74,52,0.35)';
    ctx.lineWidth = Math.max(0.4, wmax * 0.04);
    ctx.beginPath();
    ctx.moveTo(mid[0][0], mid[0][1]);
    for (i = 1; i <= N; i++) ctx.lineTo(top[i][0], top[i][1]);
    ctx.stroke();
  }

  function swordLeaves(ctx, S, g, greens, r) {
    var defs = [[-0.12, 0.55], [0.1, 0.44], [-0.32, 0.36], [0.3, 0.3]];
    for (var i = 0; i < defs.length; i++) {
      var p = span(g, 0.05 + i * 0.06, 0.5 + i * 0.06);
      var a = defs[i][0] + (r() - 0.5) * 0.1, len = defs[i][1] * S * (0.9 + r() * 0.2);
      if (p <= 0) continue;
      ctx.save();
      ctx.translate((r() - 0.5) * S * 0.03, 0);
      ctx.rotate(-PI / 2 + a);
      ctx.scale(p, 1);
      leafBlade(ctx, len, S * 0.032, 0, 0, a > 0 ? -0.08 : 0.08, 1, greens, false);
      ctx.restore();
    }
  }

  /* ---------- sparkles and glow ---------- */

  // Twinkling four point stars around a bloom (glitter boxes).
  function drawSparkles(ctx, x, y, rad, time, seed) {
    var r = rng(seed ^ 0x9e3779b9);
    for (var i = 0; i < 5; i++) {
      var a = r() * TAU, d = Math.sqrt(r()) * rad * 0.85, ph = r() * TAU, sp = 0.7 + r() * 0.6;
      var s = Math.sin(time / 900 * sp + ph);
      if (s < 0.35) continue;
      var k = (s - 0.35) / 0.65, sz = rad * 0.22 * k;
      var sx = x + Math.cos(a) * d, sy = y + Math.sin(a) * d * 0.75;
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(time / 1400 + ph);
      ctx.globalAlpha = k;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.moveTo(0, -sz); ctx.lineTo(sz * 0.22, -sz * 0.22); ctx.lineTo(sz, 0); ctx.lineTo(sz * 0.22, sz * 0.22);
      ctx.lineTo(0, sz); ctx.lineTo(-sz * 0.22, sz * 0.22); ctx.lineTo(-sz, 0); ctx.lineTo(-sz * 0.22, -sz * 0.22);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /* ---------- public API ---------- */

  function resolve(opts) {
    var key = SPECIES[opts.species] ? opts.species : 'peony';
    var S = +opts.size > 0 ? +opts.size : 100;
    var R = S * SPECIES[key].headRatio * 0.58;
    return { key: key, S: S, R: R, T: TRAITS[key], L: S - R * TRAITS[key].top };
  }

  // Bounding box of a fully grown flower (css px relative to the stem base).
  function bounds(opts) {
    var b = resolve(opts || {});
    // Tulip leaves arch out from the base a little wider than other species' leaves.
    var half = Math.max(b.R * 1.22, b.S * (b.key === 'tulip' ? 0.37 : 0.34)) + 3;
    return { left: -half, top: -(b.S * 1.08 + 3), width: half * 2, height: b.S * 1.08 + b.S * 0.05 + 6 };
  }

  function drawFlower(ctx, opts) {
    opts = opts || {};
    var b = resolve(opts), key = b.key, S = b.S, R = b.R, T = b.T;
    var g = opts.growth == null ? 1 : c01(+opts.growth || 0);
    var seed = (opts.seed | 0) >>> 0;
    var greens = GREENS[T.greens];
    var c = rgb(opts.color || '#E99A8C');
    var acc = opts.accent ? rgb(opts.accent) : deepOf(c);
    var alpha = opts.alpha == null ? 1 : c01(+opts.alpha);
    var glass = alpha < 0.55;
    LIFT = liftFor(c);
    var rStem = rng(seed * 3 + 1), rLeaf = rng(seed * 5 + 2), rHead = rng(seed * 7 + 3);
    var curve = stemCurve(b.L, rStem, T.straight);
    var geo = { hx: 0, hy: 0, hr: 0 };

    ctx.save();
    if (opts.sway) ctx.rotate(opts.sway);
    ctx.lineJoin = 'round';

    if (opts.shadow !== false) {
      ctx.fillStyle = radial(ctx, 0, 0, S * 0.09, [0, 'rgba(30,24,14,0.28)', 1, 'rgba(30,24,14,0)']);
      ctx.save(); ctx.scale(1, 0.3); ctx.beginPath(); ctx.arc(0, 0, S * 0.09, 0, TAU); ctx.fill(); ctx.restore();
    }
    if (T.leaf[0] === 'sword') swordLeaves(ctx, S, g, greens, rLeaf);
    // Tulip: the larger leaf stands behind the stem, the smaller one wraps in front of it.
    var strap = T.leaf[0] === 'strap', lside = 1, lk = 1;
    if (strap) {
      lside = rLeaf() < 0.5 ? -1 : 1; lk = 0.9 + rLeaf() * 0.14;
      var lp0 = span(g, 0.22, 0.52);
      if (lp0 > 0) strapLeaf(ctx, S, lside, lk, lp0, greens, S >= 60);
    }

    var prog = span(g, 0, 0.38);
    var tip = [0, 0];
    if (prog > 0) tip = drawStem(ctx, curve, prog, Math.max(1.1, S * 0.022 * T.stemW), greens, T.hairy);
    leaves(ctx, key, T, S, g, curve, greens, S >= 60, rLeaf);
    if (strap) {
      var lp1 = span(g, 0.3, 0.6);
      if (lp1 > 0) strapLeaf(ctx, S, -lside, lk * 0.82, lp1, greens, S >= 60);
    }

    var hp = span(g, 0.33, 0.45);
    if (hp > 0.01) {
      var p2 = bez(curve, 0.98), nod = Math.atan2(tip[0] - p2[0], p2[1] - tip[1]) * 0.6 + (rHead() - 0.5) * 0.14;
      var hs = R * hp;
      var hcy = T.hcy || 0.3; // head center above the stem tip, in head radii
      geo = { hx: tip[0], hy: tip[1] - R * hcy * hp, hr: R * hp };
      ctx.save();
      ctx.translate(tip[0], tip[1]);
      if (opts.glow > 0) {
        ctx.fillStyle = radial(ctx, 0, -R * hcy, R * 2.4, [0, 'rgba(255,246,220,' + (0.7 * opts.glow) + ')', 0.45, 'rgba(255,236,190,' + (0.3 * opts.glow) + ')', 1, 'rgba(255,236,190,0)']);
        ctx.beginPath(); ctx.arc(0, -R * hcy, R * 2.4, 0, TAU); ctx.fill();
      }
      ctx.rotate(nod);
      ctx.scale(hs, hs);
      var img = opts.image;
      if (img && img.complete && img.naturalWidth) {
        // Image mode: the cutout is centered on the stem tip and grows like a bloom.
        var sc = 0.45 + 0.55 * ease((g - 0.42) / 0.58), iw = 2.3 * sc, ih = iw * img.naturalHeight / img.naturalWidth;
        ctx.drawImage(img, -iw / 2, -ih * 0.62, iw, ih);
      } else {
        var P = {
          c: c, acc: acc, alpha: alpha, glass: glass, glitter: !!opts.glitter, greens: greens,
          open: c01((g - 0.42) / 0.58), px: 1 / hs, detail: S * hp >= 40,
          veinCol: css(mix(c, acc, 0.55)), edgeCol: glass ? 'rgba(255,255,255,1)' : css(mix(c, acc, 0.65))
        };
        if (glass) { // faint cool body so the clear rose reads against light skies
          var gk = 0.35 + 0.65 * c01((g - 0.42) / 0.58);
          ctx.fillStyle = 'rgba(150,165,175,0.16)';
          ctx.beginPath(); ctx.ellipse(0, -0.12, 0.85 * gk, 0.7 * gk, 0, 0, TAU); ctx.fill();
        }
        if (key !== 'iris' && key !== 'peony-bud' && key !== 'daffodil' && key !== 'tulip') sepals(ctx, P, 5, 0.42, 0.12, T.greens, rHead, 150);
        if (key === 'peony') peony(ctx, P, rHead);
        else if (key === 'peony-bud') peonyBud(ctx, P, rHead);
        else if (key === 'cosmos') cosmos(ctx, P, rHead);
        else if (key === 'poppy') poppy(ctx, P, rHead);
        else if (key === 'iris') iris(ctx, P, rHead);
        else if (key === 'garden-rose' || key === 'rose') rose(ctx, P, rHead);
        else if (key === 'tulip') tulip(ctx, P, rHead);
        else if (key === 'daisy') daisy(ctx, P, rHead);
        else if (key === 'daffodil') daffodil(ctx, P, rHead);
        else zinnia(ctx, P, rHead);
        if (glass) { // glass highlight streak
          ctx.strokeStyle = 'rgba(255,255,255,0.9)';
          ctx.lineWidth = 1.6 / hs;
          ctx.lineCap = 'round';
          ctx.beginPath(); ctx.arc(-0.12, -0.2, 0.55, PI * 1.08, PI * 1.42); ctx.stroke();
        }
      }
      ctx.restore();
      if (opts.glitter && typeof opts.time === 'number') drawSparkles(ctx, geo.hx, geo.hy, geo.hr * 1.1, opts.time, seed);
    }
    ctx.restore();
    return geo;
  }

  function loadImages(config) {
    var urls = [];
    try {
      var cfg = config || window.BloomConfig || {};
      var add = function (u) { if (u && typeof u === 'string' && urls.indexOf(u) < 0) urls.push(u); };
      Object.keys(cfg.boxes || {}).forEach(function (k) { add((cfg.boxes[k] || {}).image); });
      Object.keys(cfg.species || {}).forEach(function (k) { add((cfg.species[k] || {}).image); });
    } catch (e) { /* ignore */ }
    var map = new Map();
    if (typeof Promise === 'undefined') return null;
    return Promise.all(urls.map(function (u) {
      return new Promise(function (done) {
        var img = new Image(), t = setTimeout(done, 8000);
        img.decoding = 'async';
        img.onload = function () { clearTimeout(t); map.set(u, img); done(); };
        img.onerror = function () { clearTimeout(t); done(); };
        img.src = u;
      });
    })).then(function () { return map; }, function () { return map; });
  }

  function styleFor(entry) {
    entry = entry || {};
    var col = typeof entry === 'string' ? entry : (entry.hex || entry.color || '#E99A8C');
    var a = entry.alpha == null ? 1 : c01(+entry.alpha);
    return { color: col, accent: entry.accent || hex(deepOf(rgb(col))), alpha: a, glass: a < 0.55 };
  }

  window.BloomFlowers = {
    version: 1,
    species: SPECIES,
    drawFlower: drawFlower,
    loadImages: loadImages,
    styleFor: styleFor,
    // Extras used by the meadow engine
    bounds: bounds,
    drawSparkles: drawSparkles,
    rgb: rgb,
    mix: mix
  };
})();
